import React from 'react';
import { 
  FileText, 
  Building2, 
  Clock,
  Users,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useGetDocumentsQuery, useGetVendorsQuery } from '../store/api/apiSlice';
import { useSelector } from 'react-redux';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;

  // Documents: fetch a page for totals and recent
  const { data: docsList } = useGetDocumentsQuery({ skip: 0, limit: 10 }, { skip: !isAuthReady });
  const { data: allDocsList } = useGetDocumentsQuery({ skip: 0, limit: 1 }, { skip: !isAuthReady });
  const totalDocuments = allDocsList?.total ?? 0;

  // Vendors: fetch total count (active vendors only, including system vendors)
  const { data: vendorsList } = useGetVendorsQuery({ 
    skip: 0, 
    limit: 1,
    include_system: true
  }, { skip: !isAuthReady });
  const totalVendors = vendorsList?.total ?? 0;
  

  // Processing queue size
  const processingDocs = (docsList?.items || []).filter(d => d.processing_status === 'processing').length;

  const recentDocuments = docsList?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Overview of your contract management system
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalDocuments}</p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Vendors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalVendors}</p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Processing Queue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{processingDocs}</p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>

        {/* Placeholder for future metric to keep 4-up layout */}
        <div className="card hidden lg:block" />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Documents</h3>
            <p className="card-description">
              Latest uploaded and processed documents
            </p>
          </div>
          <div className="space-y-4">
            {recentDocuments.map((doc) => (
              <Link key={doc.id} to={`/documents/${doc.id}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded">
                    <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {doc.title || doc.original_filename}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {doc.contract_type || doc.document_type || 'Contract'} • {new Date(doc.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className={`badge ${
                  doc.processing_status === 'analyzed' || doc.processing_status === 'completed' ? 'badge-success' :
                  doc.processing_status === 'processing' ? 'badge-warning' :
                  'badge-info'
                }`}>
                  {doc.processing_status}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quick Actions</h3>
            <p className="card-description">
              Common tasks and shortcuts
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/documents')}
              className="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400 mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Upload Document
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Add new document for analysis
              </p>
            </button>
            <button
              onClick={() => navigate('/vendors')}
              className="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400 mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Create Vendor
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Add/manage vendors
              </p>
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400 mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Manage Users
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Team and permissions
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 